import { useProfile } from "../context/ProfileContext";
import { PlayCircle } from "lucide-react";

// Convert a watch URL (YouTube / Hudl) into an embeddable URL.
// Falls back to the original if no pattern matches.
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // YouTube: watch?v=ID, youtu.be/ID, /shorts/ID
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") {
        const id = u.searchParams.get("v");
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      if (u.pathname.startsWith("/embed/") || u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/").pop();
        return `https://www.youtube.com/embed/${id}`;
      }
    }
    if (u.hostname === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    // Hudl: /video/3/USER/VIDEO → /embed/video/3/USER/VIDEO
    if (u.hostname.includes("hudl.com")) {
      if (u.pathname.startsWith("/video/")) {
        return `https://www.hudl.com${u.pathname.replace("/video/", "/embed/video/")}`;
      }
      return url; // already /embed/...
    }
    // Vimeo
    if (u.hostname.includes("vimeo.com") && !u.hostname.includes("player.")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    /* not a URL */
  }
  return url;
}

export default function HighlightReel() {
  const PROFILE = useProfile();
  const watchUrl = PROFILE.reelUrl;
  const embedUrl = toEmbedUrl(watchUrl);

  return (
    <section
      id="reel"
      className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/10"
      data-testid="reel-section"
    >
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
              Highlight Reel
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase font-bold tracking-tight text-white leading-none">
              <PlayCircle
                size={48}
                className="inline-block mb-2 mr-3 text-[#E60000]"
                strokeWidth={1.5}
              />
              See Her at the Net
            </h2>
          </div>
          <p className="max-w-md text-[#8A8A8A] text-base leading-relaxed">
            Latest verified game film — middle-blocker reps, fast-tempo attacks, and blocking sequences.
            Press play.
          </p>
        </div>

        {embedUrl ? (
          <div className="relative border border-white/10 bg-black overflow-hidden" data-testid="reel-embed">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                key={embedUrl}
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                title="Faith Ahyi-Sena — Highlight reel"
              />
            </div>
          </div>
        ) : (
          <div className="border border-white/10 p-12 text-center text-[#8A8A8A]">
            No reel URL set. Add one in the admin panel.
          </div>
        )}

        {watchUrl && (
          <div className="mt-6 flex flex-wrap items-center gap-6 text-xs tracking-[0.22em] uppercase">
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-[#E60000] transition-colors border-b border-white/30 hover:border-[#E60000] pb-1"
              data-testid="reel-open-link"
            >
              Open in new tab ↗
            </a>
            <span className="text-[#8A8A8A]">If the player does not load, click the link above</span>
          </div>
        )}
      </div>
    </section>
  );
}
